import { useState, useEffect } from "react";
import { Link, useLocation, useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import axios from "axios";
import DiceRoller from "@/components/DiceRoller";
import { 
  BookOpen, ChevronLeft, ChevronDown, ChevronRight, Menu, X, LogOut,
  FileText, Users, Skull, Scroll, Sparkles, Map, Castle, Dices, Swords, Wand2, Clock,
  UserCircle2, Package, Globe, UsersRound, Backpack, Wrench, Target, Lock, HelpCircle, History, Flag, Gamepad2, GitBranch, ShieldCheck, Heart, Pin, Star, Search, Table2,
  LayoutDashboard, Compass, Archive, Home
} from "lucide-react";
import { DMScreen } from "@/components/DMScreen";
import { UniversalSearchDialog } from "@/components/UniversalSearchDialog";
import { DocumentTabsProvider, DocumentTabBar, useDocumentTabs } from "@/components/DocumentTabs";
import CampaignBreadcrumbs from "@/components/CampaignBreadcrumbs";
import { useDragAutoScroll } from "@/hooks/useDragAutoScroll";

// Resolve an image field: pass through full URLs, else serve the storage key
// through the authenticated /api/files proxy.
const resolveImg = (v) => (!v ? null : (v.startsWith("http") ? v : `${process.env.REACT_APP_BACKEND_URL}/api/files/${v}`));

// Detect platform for shortcut display
const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent || "");
const SHORTCUT_KEY = isMac ? "⌘" : "Ctrl";

const navGroups = [
  {
    key: "overview",
    label: "Overview",
    icon: LayoutDashboard,
    items: [
      { path: "", label: "Campaign Home", icon: Home, exact: true },
      { path: "chapters", label: "Story", icon: BookOpen },
    ],
  },
  {
    key: "party",
    label: "Party",
    icon: UsersRound,
    items: [
      { path: "characters", label: "Players", icon: Users },
      { path: "npcs", label: "NPCs", icon: UserCircle2 },
      { path: "characters?type=companion", label: "Companions", icon: Heart },
      { path: "party", label: "The Party", icon: UsersRound, gated: true },
    ],
  },
  {
    key: "world",
    label: "World",
    icon: Globe,
    items: [
      { path: "maps", label: "World Atlas", icon: Compass },
      { path: "locations", label: "Locations", icon: Castle },
      { path: "factions", label: "Factions", icon: Flag },
      { path: "quests", label: "Quests", icon: Target },
      { path: "lore", label: "Lore", icon: Scroll },
      { path: "deities", label: "Deities", icon: Sparkles },
      { path: "relationships", label: "Family Trees", icon: GitBranch },
      { path: "world-history", label: "World History", icon: History },
    ],
  },
  {
    key: "vault",
    label: "Vault",
    icon: Archive,
    items: [
      { path: "items", label: "Items", icon: Package },
      { path: "creatures", label: "Creatures", icon: Skull },
      { path: "tables", label: "Tables", icon: Table2 },
      { path: "handouts", label: "Handouts", icon: FileText },
    ],
  },
  {
    key: "tools",
    label: "Tools",
    icon: Wrench,
    items: [
      { path: "vtt", label: "Virtual Tabletop", icon: Gamepad2, gated: true },
      { path: "encounter-builder", label: "Encounters", icon: Target, gated: true },
      { path: "combat", label: "Combat", icon: Swords, gated: true },
      { path: "oracle", label: "AI Oracle", icon: Wand2, gated: true },
      { path: "dm-screen", label: "DM Screen", icon: Dices, gated: true },
      { path: "wiki", label: "Player Wiki", icon: BookOpen },
      { path: "notes", label: "Personal Notes", icon: BookOpen },
    ],
  },
];

const SIDEBAR_GROUP_KEY = "natone_sidebar_groups_v1";

const readGroupState = () => {
  try {
    const raw = localStorage.getItem(SIDEBAR_GROUP_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};
const writeGroupState = (state) => {
  try { localStorage.setItem(SIDEBAR_GROUP_KEY, JSON.stringify(state)); } catch { /* ignore */ }
};

const SidebarGroup = ({ group, campaignId, isActive, onNavigate, userTier }) => {
  const hasActiveChild = group.items.some(item => isActive(item.path));
  // Default: only the "world" group is open for a brand-new user. Active section
  // is always force-opened (so navigating into Tools auto-expands Tools).
  const stored = readGroupState();
  const initialOpen = stored && Object.prototype.hasOwnProperty.call(stored, group.key)
    ? !!stored[group.key]
    : (group.key === "overview" || group.key === "world");
  const [open, setOpen] = useState(initialOpen || hasActiveChild);
  const isCommoner = userTier === "commoner";

  // Auto-open when an item inside this group becomes active (e.g. navigating in via URL)
  useEffect(() => {
    if (hasActiveChild && !open) setOpen(true);
  }, [hasActiveChild]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleOpen = () => {
    const next = !open;
    setOpen(next);
    const cur = readGroupState() || {};
    cur[group.key] = next;
    writeGroupState(cur);
  };

  return (
    <div className="mb-1" data-testid={`nav-group-${group.key}`}>
      <button
        onClick={toggleOpen}
        className="w-full flex items-center gap-2 px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70 hover:text-muted-foreground transition-colors rounded-sm"
        data-testid={`sidebar-group-toggle-${group.key}`}
      >
        <group.icon className="w-3.5 h-3.5" />
        <span className="flex-1 text-left">{group.label}</span>
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
      </button>
      {open && (
        <div className="ml-2 mt-0.5 space-y-0.5 border-l border-border/40 pl-2">
          {group.items.map((item) => {
            const isLocked = item.gated && isCommoner;
            const testKey = (item.path || "home").replace(/[?=&/]/g, "-");
            const to = item.path === "" ? `/campaign/${campaignId}` : `/campaign/${campaignId}/${item.path}`;
            return (
              <Link
                key={item.path}
                to={isLocked ? "#" : to}
                className={`sidebar-link text-sm ${isActive(item.path) ? 'active' : ''} ${isLocked ? 'opacity-60 cursor-not-allowed' : ''}`}
                data-testid={`sidebar-item-${testKey}`}
                onClick={(e) => {
                  if (isLocked) {
                    e.preventDefault();
                    toast.error("Upgrade to Adventurer or Archmage to unlock this feature.");
                    return;
                  }
                  onNavigate?.();
                }}
              >
                <item.icon className="w-4 h-4" />
                <span className="flex-1">{item.label}</span>
                {isLocked && <Lock className="w-3 h-3 text-muted-foreground/50" />}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Lightweight Pinned/Recent stub — stored per-campaign in localStorage.
// Phase A ships this as a passive list; Phase D will add right-click "pin" actions across the app.
const PinnedRecentStub = ({ campaignId }) => {
  const pinKey = `np_pinned_${campaignId}`;
  const recentKey = `np_recent_${campaignId}`;
  const [pinned, setPinned] = useState(() => { try { return JSON.parse(localStorage.getItem(pinKey)) || []; } catch { return []; } });
  const [recent, setRecent] = useState(() => { try { return JSON.parse(localStorage.getItem(recentKey)) || []; } catch { return []; } });

  // Listen for cross-component updates (e.g. when the Story page pushes a recent entry).
  useEffect(() => {
    const handler = () => {
      try { setPinned(JSON.parse(localStorage.getItem(pinKey)) || []); } catch {}
      try { setRecent(JSON.parse(localStorage.getItem(recentKey)) || []); } catch {}
    };
    window.addEventListener("storage", handler);
    window.addEventListener("np-pinned-changed", handler);
    return () => {
      window.removeEventListener("storage", handler);
      window.removeEventListener("np-pinned-changed", handler);
    };
  }, [pinKey, recentKey]);

  if (!campaignId) return null;

  return (
    <div className="px-3 py-2 border-t border-border/60 bg-muted/5" data-testid="sidebar-pinned-area">
      {pinned.length > 0 && (
        <div className="mb-2">
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground/70 mb-1">
            <Pin className="w-2.5 h-2.5" /> Pinned
          </div>
          <div className="space-y-0.5">
            {pinned.slice(0, 4).map((p) => (
              <Link
                key={p.path}
                to={`/campaign/${campaignId}/${p.path}`}
                className="block text-[11px] text-muted-foreground hover:text-primary truncate"
                data-testid={`sidebar-pinned-${p.path.replace(/[?=&/]/g, "-")}`}
                title={p.label}
              >
                {p.label}
              </Link>
            ))}
          </div>
        </div>
      )}
      {recent.length > 0 && (
        <div>
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground/70 mb-1">
            <Clock className="w-2.5 h-2.5" /> Recent
          </div>
          <div className="space-y-0.5">
            {recent.slice(0, 4).map((p) => (
              <Link
                key={p.ts}
                to={`/campaign/${campaignId}/${p.path}`}
                className="block text-[11px] text-muted-foreground hover:text-primary truncate"
                data-testid={`sidebar-recent-${p.path.replace(/[?=&/]/g, "-")}`}
                title={p.label}
              >
                {p.label}
              </Link>
            ))}
          </div>
        </div>
      )}
      {pinned.length === 0 && recent.length === 0 && (
        <p className="text-[10px] italic text-muted-foreground/50">Pinned & recent pages will appear here.</p>
      )}
    </div>
  );
};

// Tiny helper that lives INSIDE DocumentTabsProvider so useDocumentTabs()
// resolves to the real context. Lets pages override their doc-tab label by
// passing `tabLabel` as a Layout prop (e.g. an entity name).
const DocTabLabelSetter = ({ label }) => {
  const { setCurrentTabLabel } = useDocumentTabs();
  useEffect(() => {
    if (label) setCurrentTabLabel(label);
  }, [label, setCurrentTabLabel]);
  return null;
};

export const Layout = ({ children, campaignName, tabLabel, hideBreadcrumbs = false, breadcrumbExtra = [] }) => {
  const { campaignId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [campaignImage, setCampaignImage] = useState(null);
  const [fetchedName, setFetchedName] = useState(null);
  const [dmScreenOpen, setDmScreenOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  // Auto-scroll the page when an HTML5 drag approaches the viewport edge,
  // so users can drop onto offscreen folder targets at the top of the page.
  useDragAutoScroll();

  // Global keyboard shortcuts (Cmd+K / Ctrl+K → search · / → search when not typing · Esc handled by Radix)
  useEffect(() => {
    const onKey = (e) => {
      const inField = ["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName) ||
        document.activeElement?.isContentEditable;
      if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setSearchOpen(o => !o);
      } else if (e.key === "/" && !inField) {
        e.preventDefault();
        setSearchOpen(true);
      } else if (e.key === "Escape" && searchOpen) {
        setSearchOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [searchOpen]);
  const embedded = new URLSearchParams(location.search).get("embedded") === "1";
  const resolvedCampaignName = campaignName || fetchedName;

  useEffect(() => {
    if (campaignId) {
      axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/campaigns/${campaignId}`, { withCredentials: true })
        .then(res => { setCampaignImage(resolveImg(res.data?.image_url)); setFetchedName(res.data?.name || null); }).catch(() => {});
    }
  }, [campaignId]);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/campaigns/${campaignId}/upload-image`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      setCampaignImage(resolveImg(res.data.image_url));
    } catch {}
  };

  const isActive = (path) => {
    // Overview home — exact match on the campaign root.
    if (path === "") {
      return location.pathname === `/campaign/${campaignId}` || location.pathname === `/campaign/${campaignId}/`;
    }
    // For paths with query (e.g. characters?type=companion), match both pathname and the query.
    if (path.includes("?")) {
      const [p, q] = path.split("?");
      return location.pathname.includes(`/${p}`) && location.search.includes(q);
    }
    return location.pathname.endsWith(`/${path}`) || location.pathname.includes(`/${path}/`);
  };

  return (
    <div className="min-h-screen bg-background flex" data-testid="campaign-layout">
      {/* Mobile menu button */}
      {!embedded && (
      <Button
        variant="ghost"
        size="icon"
        className="fixed top-4 left-4 z-50 md:hidden"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        data-testid="mobile-menu-button"
      >
        {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </Button>
      )}

      {/* Sidebar */}
      {!embedded && (
      <aside 
        className={`sidebar fixed md:sticky top-0 left-0 h-screen w-60 z-40 transform transition-transform duration-300 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo + Search trigger */}
          <div className="p-4 border-b border-border flex items-center gap-2">
            <Link to="/dashboard" className="flex items-center gap-2 flex-1 min-w-0">
              <img src="/logo.png" alt="NaturalOne Chronicles" className="w-8 h-8 rounded-sm object-contain" />
              <span className="text-sm font-serif text-foreground truncate">
                Natural<span className="text-primary">One</span>
              </span>
            </Link>
            <button
              onClick={() => setSearchOpen(true)}
              className="flex items-center gap-1 px-1.5 py-1 text-[10px] text-muted-foreground/70 hover:text-foreground rounded-sm border border-border/50 hover:border-border bg-muted/10"
              title={`Universal Search (${SHORTCUT_KEY} K)`}
              data-testid="universal-search-trigger"
            >
              <Search className="w-3 h-3" />
              <kbd className="font-sans">{SHORTCUT_KEY}K</kbd>
            </button>
          </div>

          {/* Campaign header */}
          <div className="border-b border-border relative">
            {campaignImage && (
              <div className="h-20 w-full overflow-hidden">
                <img src={campaignImage} alt="" className="w-full h-full object-cover opacity-60" onError={() => setCampaignImage(null)} />
              </div>
            )}
            <div className={`p-4 ${campaignImage ? "-mt-10 relative" : ""}`}>
              <Button
                variant="ghost"
                className="w-full justify-start text-muted-foreground hover:text-foreground p-0 h-auto"
                onClick={() => navigate('/dashboard')}
                data-testid="back-to-campaigns"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                <span className="text-xs">Campaigns</span>
              </Button>
              <h2 className="font-serif text-lg text-foreground mt-2 line-clamp-2">
                {resolvedCampaignName || "Campaign"}
              </h2>
              <input type="file" accept="image/*" className="hidden" id="campaign-image-upload" onChange={handleImageUpload} />
              <label htmlFor="campaign-image-upload" className="text-[9px] text-muted-foreground hover:text-primary cursor-pointer mt-1 inline-block" data-testid="upload-campaign-image">
                {campaignImage ? "Change image" : "Add header image"}
              </label>
            </div>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 px-3 py-3" data-testid="campaign-sidebar">
            <nav className="space-y-0.5">
              {navGroups.map((group) => (
                <SidebarGroup
                  key={group.key}
                  group={group}
                  campaignId={campaignId}
                  isActive={isActive}
                  onNavigate={() => setSidebarOpen(false)}
                  userTier={user?.tier || "commoner"}
                />
              ))}
            </nav>
          </ScrollArea>

          {/* Pinned & Recent */}
          <PinnedRecentStub campaignId={campaignId} />

          {/* User section */}
          <div className="p-4 border-t border-border">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground truncate max-w-[140px]">
                {user?.email}
              </span>
              <div className="flex items-center gap-0.5">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-primary"
                  onClick={() => setDmScreenOpen(true)}
                  data-testid="sidebar-dm-screen"
                  title="DM Screen — Quick rules reference"
                >
                  <ShieldCheck className="w-4 h-4" />
                </Button>
                <Link to="/help">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-foreground"
                    data-testid="sidebar-help"
                    title="Help & Guides"
                  >
                    <BookOpen className="w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/support">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-foreground"
                    data-testid="sidebar-support"
                    title="Support"
                  >
                    <HelpCircle className="w-4 h-4" />
                  </Button>
                </Link>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={logout}
                  className="h-8 w-8 text-muted-foreground hover:text-foreground"
                  data-testid="sidebar-logout"
                >
                  <LogOut className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </aside>
      )}

      {/* Mobile overlay */}
      {!embedded && sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="flex-1 min-w-0 min-h-screen md:ml-0 flex flex-col overflow-x-hidden">
        {!embedded && (
          <DocumentTabsProvider>
            {campaignId && (
              <div className="hidden md:flex sticky top-0 z-20 bg-background/95 backdrop-blur border-b border-border px-4 md:px-6 py-2 items-center gap-3">
                <button
                  onClick={() => setSearchOpen(true)}
                  data-testid="top-search-bar"
                  className="group flex items-center gap-2 flex-1 max-w-xl h-9 px-3 rounded-md border border-border/60 bg-muted/10 text-muted-foreground/70 hover:border-primary/40 hover:text-foreground transition-colors"
                >
                  <Search className="w-4 h-4" />
                  <span className="text-sm flex-1 text-left truncate">Search this campaign — NPCs, locations, items, lore…</span>
                  <kbd className="text-[10px] font-sans border border-border/50 rounded px-1 py-0.5">{SHORTCUT_KEY}K</kbd>
                </button>
              </div>
            )}
            <DocumentTabBar />
            {/* Page-supplied tab label override (e.g. an entity name). Lives
                inside the provider so useDocumentTabs() returns the real
                context, not the no-op fallback. */}
            {tabLabel && <DocTabLabelSetter label={tabLabel} />}
            <div className="p-4 md:p-6 pt-16 md:pt-4 w-full min-w-0 flex-1">
              {campaignId && !hideBreadcrumbs && (
                <CampaignBreadcrumbs campaignName={resolvedCampaignName} extraCrumbs={breadcrumbExtra} />
              )}
              {children}
            </div>
          </DocumentTabsProvider>
        )}
        {embedded && (
          <div className="p-3 min-w-0 flex-1">{children}</div>
        )}
      </main>
      {!embedded && <DiceRoller />}
      <DMScreen open={dmScreenOpen} onOpenChange={setDmScreenOpen} />
      <UniversalSearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
    </div>
  );
};

export default Layout;
